<script setup lang="ts">
import { ref } from 'vue';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import { useUserActionsTooltips } from '@/composables/user-actions-tooltips';
import { useUserManagement } from '@/composables/user-management';

import Button from 'primevue/button';
import Menu from 'primevue/menu';

// -----
// Props & Emits
// -----

const props = defineProps<{
  user: UserResponseDto;
  orientation?: 'vertical';
}>();

const emit = defineEmits<{
  deleteUser: [user: UserResponseDto];
}>();

// -----
// Setup
// -----

const menu = ref<Menu>();
const menuUserId = ref<string>();
const menuItems = ref([{ label: 'Delete Account', icon: 'pi pi-trash' }]);

const { canDeleteUser } = useUserManagement(menuUserId);
const { deletionBlockerTooltipText } = useUserActionsTooltips(menuUserId);

// -----
// Methods
// -----

const toggle = (event: Event) => {
  menuUserId.value = props.user.id;
  menu.value?.toggle(event);
};

const handleDeleteUser = () => {
  if (canDeleteUser.value) {
    emit('deleteUser', props.user);
  }
};
</script>

<template>
  <div class="user-actions-menu">
    <Button
      type="button"
      :icon="
        props.orientation === 'vertical'
          ? 'pi pi-ellipsis-v'
          : 'pi pi-ellipsis-h'
      "
      @click="toggle"
      aria-haspopup="true"
      aria-controls="overlay_menu"
      :class="[
        'ellipsis-button',
        { 'ellipsis-button-circular': props.orientation === 'vertical' },
      ]"
    />
    <Menu ref="menu" id="overlay_menu" :model="menuItems" :popup="true">
      <template #start>
        <span class="menu-header"><strong>Options</strong></span>
      </template>
      <template #item="{ item }">
        <a
          v-ripple
          v-tooltip.bottom.hover="deletionBlockerTooltipText"
          class="menu-item"
          @click="handleDeleteUser()"
          :class="{ 'menu-item-disabled': !canDeleteUser }"
        >
          <span :class="item.icon" />
          <span class="ml-2">{{ item.label }}</span>
        </a>
      </template>
    </Menu>
  </div>
</template>

<style lang="less" scoped>
.ellipsis-button {
  background-color: transparent !important;
  border: none !important;
  color: #374151 !important;
  box-shadow: none !important;

  &:hover,
  &:focus {
    background-color: #e3e4e9 !important;
  }
}

.ellipsis-button-circular {
  border-radius: 50% !important;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0 !important;

  &:hover,
  &:focus {
    background-color: #e3e4e9 !important;
    border-radius: 50% !important;
  }
}

.menu-header {
  color: #000;
  padding: 0.5rem 0.75rem;
  display: block;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  color: #000;
  cursor: pointer;
  width: 100%;

  &:hover:not(.menu-item-disabled) {
    background-color: #f3f4f6;
  }
}

.menu-item-disabled {
  opacity: 0.5;
  cursor: not-allowed;

  &:hover {
    background-color: transparent;
  }
}
</style>
